/**
 * FluxoGerencial Mutation Hooks
 *
 * Custom hooks for fluxoGerencial mutations.
 * Uses direct Amplify calls (same pattern as progress-hooks.ts)
 * because fluxoGerencial types are not yet in the @ds/ds-api-types package.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { post } from 'aws-amplify/api'
import { useToast } from '@/hooks/use-toast'

// ============================================================================
// Types
// ============================================================================

interface UpdateInput {
  id: number
  data: Date
  valor: number
  centroResultado: number | null
  numeroNota: string | null
  natureza: string | null
  centroCusto: string | null
}

interface DeleteInput {
  id: number
}

interface GetUploadUrlInput {
  fileName: string
  contentType: string
}

interface GetUploadUrlOutput {
  signedUrl: string
  s3Key: string
  expiresIn: number
}

interface ProcessImportInput {
  s3Key: string
  fileName: string
}

export interface CachePeriod {
  year: number
  month: number
}

interface ProcessImportOutput {
  importacaoId: number
  rowCount: number
  fileName: string
  affectedPeriods: CachePeriod[]
  snapshotId?: string
}

interface EnqueueDreCacheRefreshInput {
  periods: CachePeriod[]
  source: 'processImport' | 'updateMany' | 'manual'
  snapshotId?: string
}

interface EnqueueDreCacheRefreshOutput {
  success: boolean
  messageId?: string
  periods: CachePeriod[]
}

interface PreviewImportInput {
  s3Key: string
}

export interface PreviewSampleRow {
  Data: string | Date
  Memo: string | null
  Valor: number
  Natureza: string | null
  CentroResultado: number | null
  Empresa: string | null
}

export interface PreviewImportOutput {
  totalAttempted: number
  validRows: number
  skippedRows: number
  warningRows: number
  sample: PreviewSampleRow[]
}

export interface BatchUpdateFields {
  Data?: string | Date
  Valor?: number
  CentroResultado?: number | null
  NumeroNota?: string | null
  Natureza?: string | null
  CentroCusto?: string
  Empresa?: string | null
  Descricao?: string | null
}

interface BatchUpdateInput {
  updates: Array<{ id: number; fields: BatchUpdateFields }>
  deletes: number[]
}

interface BatchUpdateOutput {
  success: boolean
  updatedCount: number
  deletedCount: number
  failed: Array<{ id: number; reason: string }>
  affectedPeriods: CachePeriod[]
  snapshotId?: string
}

// ============================================================================
// Amplify fetch helper
// ============================================================================

/**
 * Fire-and-forget enqueue of a DRE cache refresh request. Called from the
 * onSuccess of `useProcessImport` and `useBatchUpdateFluxoGerencial` because
 * the producer Lambdas are inside VPC and cannot reach SQS — the dedicated
 * `enqueueDreCacheRefresh` endpoint runs OUTSIDE VPC.
 *
 * Failures are logged but never thrown back at the caller; the actual write
 * already succeeded, and a missed enqueue can be re-issued manually.
 */
async function fireAndForgetDreCacheRefresh(input: EnqueueDreCacheRefreshInput): Promise<void> {
  if (!input.periods || input.periods.length === 0) return
  try {
    await fluxoPost<EnqueueDreCacheRefreshOutput>('/enqueueDreCacheRefresh', input)
  } catch (err) {
    // Don't surface to user — write succeeded; refresh is best-effort here.
    console.error('[gerencial] enqueueDreCacheRefresh failed:', err)
  }
}

async function fluxoPost<T>(path: string, input: unknown): Promise<T> {
  const fullPath = `/fluxoGerencial${path}`

  const restOperation = post({
    apiName: 'monumenta-trpc',
    path: fullPath,
    options: {
      body: input as Record<string, unknown>,
    } as Parameters<typeof post>[0]['options'],
  })

  const response = await restOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body)

  if (parsed.result?.data) return parsed.result.data
  if (parsed.error) throw new Error(parsed.error.message || 'Unknown error')
  return parsed
}

// ============================================================================
// Hooks
// ============================================================================

export function useUpdateFluxoGerencial() {
  const queryClient = useQueryClient()
  const { success, error } = useToast()

  return useMutation({
    mutationFn: (input: UpdateInput) => fluxoPost('/update', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fluxoGerencial'] })
      success('Registro atualizado com sucesso')
    },
    onError: (err: Error) => {
      error(`Erro ao atualizar registro: ${err.message}`)
    },
  })
}

export function useDeleteFluxoGerencial() {
  const queryClient = useQueryClient()
  const { success, error } = useToast()

  return useMutation({
    mutationFn: (input: DeleteInput) => fluxoPost('/delete', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fluxoGerencial'] })
      success('Registro excluído com sucesso')
    },
    onError: (err: Error) => {
      error(`Erro ao excluir registro: ${err.message}`)
    },
  })
}

export function usePreviewImport() {
  return useMutation({
    mutationFn: (input: PreviewImportInput) =>
      fluxoPost<PreviewImportOutput>('/previewImport', input),
    retry: false,
  })
}

export function useGetUploadUrl() {
  return useMutation({
    mutationFn: (input: GetUploadUrlInput) =>
      fluxoPost<GetUploadUrlOutput>('/getUploadUrl', input),
    retry: false,
  })
}

export function useProcessImport() {
  const queryClient = useQueryClient()
  const { success, error } = useToast()

  return useMutation({
    mutationFn: (input: ProcessImportInput) =>
      fluxoPost<ProcessImportOutput>('/processImport', input),
    retry: false,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fluxoGerencial'] })
      success(`${data.rowCount} registros importados com sucesso`)
      // The producer (this endpoint) is in VPC and can't reach SQS — kick the
      // refresh from the non-VPC `enqueueDreCacheRefresh` endpoint.
      void fireAndForgetDreCacheRefresh({
        periods: data.affectedPeriods,
        source: 'processImport',
        snapshotId: data.snapshotId,
      })
    },
    onError: (err: Error) => {
      error(`Erro na importação: ${err.message}`)
    },
  })
}

/**
 * Batch update — sends a payload of dirty rows to the server in a single
 * HTTP call. Server applies updates and triggers ONE DRE cache rebuild
 * for the affected year set (fire-and-forget).
 *
 * The hook does NOT auto-toast — the modo edição flow uses a custom
 * confirm/saving dialog and shows its own status messages.
 * onSuccess invalidates queries so the table refreshes after save.
 */
export function useBatchUpdateFluxoGerencial() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: BatchUpdateInput) =>
      fluxoPost<BatchUpdateOutput>('/updateMany', input),
    retry: false,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fluxoGerencial'] })
      // Same pattern as useProcessImport: enqueue the DRE refresh from the
      // non-VPC endpoint. `affectedYears` already includes years from updates
      // AND deletes (computed server-side in updateMany).
      void fireAndForgetDreCacheRefresh({
        periods: data.affectedPeriods,
        source: 'updateMany',
        snapshotId: data.snapshotId,
      })
    },
  })
}
